import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetTrackerJobsQuery, useSearchTrackerJobsQuery } from '../../../redux/apis/trackerApi';
import TrackerFiltersDrawer from '../TrackerFiltersDrawer/TrackerFiltersDrawer';
import TrackerToolbar from '../TrackerToolbar/TrackerToolbar';

import TrackerBoardView from './TrackerBoardView';
import TrackerListView from './TrackerListView';

const STATUSES = ['new', 'applied', 'interview', 'archived'];
const MOBILE_BREAKPOINT = 768;

/** Auto-switch to list view on mobile screens */
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return isMobile;
};

const TrackerBoard = ({ direction }) => {
  const isMobile = useIsMobile();

  const [viewMode, setViewMode] = useState(() => localStorage.getItem('trackerViewMode') || 'board');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({});

  const effectiveViewMode = isMobile ? 'list' : viewMode;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Persist view mode
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
    localStorage.setItem('trackerViewMode', mode);
  }, []);

  // Fetch all 4 status columns — backend returns all jobs at once (no pagination)
  const newQuery = useGetTrackerJobsQuery({ direction, status: 'new' });
  const appliedQuery = useGetTrackerJobsQuery({ direction, status: 'applied' });
  const interviewQuery = useGetTrackerJobsQuery({ direction, status: 'interview' });
  const archivedQuery = useGetTrackerJobsQuery({ direction, status: 'archived' });

  // Server-side search
  const searchResult = useSearchTrackerJobsQuery({ query: debouncedQuery }, { skip: !debouncedQuery });

  const isSearching = !!debouncedQuery;

  const isAnyColumnLoading =
    newQuery.isLoading || appliedQuery.isLoading || interviewQuery.isLoading || archivedQuery.isLoading;
  const isLoading = isAnyColumnLoading;

  /** Deduplicate flattened jobs by composite job+referral key */
  const dedup = (jobs) => {
    const seen = new Set();
    return jobs.filter((j) => {
      const jobId = j.id || j.uuid;
      const refId = j.referrals?.[0]?.uuid || j.referrals?.[0]?.id;
      const key = refId ? `${jobId}-${refId}` : jobId;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const columns = useMemo(() => {
    const queryMap = { new: newQuery, applied: appliedQuery, interview: interviewQuery, archived: archivedQuery };

    if (isSearching && searchResult.data) {
      const queryLower = debouncedQuery.toLowerCase();

      const serverJobs = direction === 'my' ? searchResult.data.myJobs : searchResult.data.theirJobs;
      const serverList = Array.isArray(serverJobs) ? serverJobs : [];

      const serverGrouped = { new: [], applied: [], interview: [], archived: [] };
      serverList.forEach((job) => {
        const candidateStatus = job.referrals?.[0]?.candidate_status || 'new';
        let bucket = 'archived';
        if (candidateStatus.includes('interview')) {
          bucket = 'interview';
        } else if (serverGrouped[candidateStatus]) {
          bucket = candidateStatus;
        }
        serverGrouped[bucket].push(job);
      });

      return STATUSES.map((status) => {
        const loaded = queryMap[status].data?.jobs || [];
        const clientMatches = loaded.filter((job) => {
          const titleMatch = (job.title || job.job_title || '').toLowerCase().includes(queryLower);
          const nameMatch = direction === 'their'
            ? (job.referrals?.[0]?.full_name || '').toLowerCase().includes(queryLower)
            : (job.referrals || []).some((r) => (r.full_name || '').toLowerCase().includes(queryLower));
          return titleMatch || nameMatch;
        });

        const merged = dedup([...serverGrouped[status], ...clientMatches]);

        return {
          status,
          jobs: merged,
          count: merged.length,
          isLoading: searchResult.isLoading || isAnyColumnLoading,
          isFetching: searchResult.isFetching,
          isLastPage: true,
        };
      });
    }

    return STATUSES.map((status) => {
      const q = queryMap[status];
      const jobs = q.data?.jobs || [];
      return {
        status,
        jobs,
        count: jobs.length,
        isLoading: isAnyColumnLoading,
        isFetching: q.isFetching,
        isLastPage: true,
      };
    });
  }, [
    isSearching,
    debouncedQuery,
    direction,
    searchResult.data,
    searchResult.isLoading,
    searchResult.isFetching,
    isAnyColumnLoading,
    newQuery,
    appliedQuery,
    interviewQuery,
    archivedQuery,
  ]);

  // Active filter count
  const filtersCount = Object.values(filters).filter((v) => v && (Array.isArray(v) ? v.length > 0 : true)).length;

  // Client-side filter application
  const filteredColumns = useMemo(() => {
    if (filtersCount === 0) return columns;

    return columns.map((col) => {
      let filtered = [...col.jobs];

      if (filters.positions?.length) {
        filtered = filtered.filter((job) =>
          filters.positions.some(
            (p) =>
              job.title?.toLowerCase().includes(p.toLowerCase()) ||
              job.job_title?.toLowerCase().includes(p.toLowerCase())
          )
        );
      }

      if (filters.companies?.length) {
        filtered = filtered.filter((job) =>
          filters.companies.some((c) => job.organization_name?.toLowerCase().includes(c.toLowerCase()))
        );
      }

      return { ...col, jobs: filtered, count: filtered.length };
    });
  }, [columns, filters, filtersCount]);

  const handleRefresh = useCallback(() => {
    newQuery.refetch();
    appliedQuery.refetch();
    interviewQuery.refetch();
    archivedQuery.refetch();
  }, [newQuery, appliedQuery, interviewQuery, archivedQuery]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <TrackerToolbar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        viewMode={effectiveViewMode}
        onViewModeChange={handleViewModeChange}
        filtersCount={filtersCount}
        onFiltersToggle={() => setFiltersOpen(true)}
        onRefresh={handleRefresh}
        isSearching={isSearching}
        isSearchLoading={searchResult.isFetching}
        direction={direction}
        isMobile={isMobile}
      />

      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {effectiveViewMode === 'board' ? (
          <TrackerBoardView
            columns={filteredColumns}
            direction={direction}
            isLoading={isLoading}
          />
        ) : (
          <TrackerListView columns={filteredColumns} direction={direction} isLoading={isLoading} />
        )}
      </div>

      <TrackerFiltersDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filters}
        onApplyFilters={setFilters}
        filtersCount={filtersCount}
        direction={direction}
      />
    </div>
  );
};

export default TrackerBoard;
